import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Leads } from '../../api/Endpoints/Leads';
import SelectX from '../Form/SelectX';
import { fetchCountries, fetchLeads, fetchProjects, fetchStatus, fetchProfessionals, fetchAgeList } from '../CommonFunctions/DropdownLists';
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress, Grid } from '@mui/material';
import TextInput from '../Form/TextInput';
import DateIn from '../Form/Date';
import { DynamicdropdownsAPI } from '../../api/Endpoints/DynamicDropdown';
import moment from 'moment';
import { toast } from 'react-toastify';
import { LoadingButton } from "@mui/lab";
import { Notes } from '../../api/Endpoints/Notes'; // Add Notes API

const dialogStyle = {
    '& .MuiDialog-paper': {
        width: '700px', // Set fixed width here
        maxWidth: '700px', // Ensure maxWidth is the same to keep it fixed
    }
};

export default function QuickEdit({ open, setOpen, id, notesTabActive, refreshNotes, triggerNotesRefresh, refreshLeadsTable }) {
    const handleClose = () => {
        setOpen(false);
    };
    
    const [loading, setLoading] = React.useState(false);
    const [statusList, setStatusList] = React.useState([]);
    const [leadId, setLeadId] = React.useState(id);
    const [notesList, setNotesList] = React.useState([]); // Add state for notes list
    const [notesLoading, setNotesLoading] = React.useState(false); // Add state for notes loading
    
    // Cache for fetched data to prevent repeated API calls
    const fetchedDataCache = React.useRef({});

    const scheme = yup.object().shape({
        crm_status: yup.object().required("status is required").typeError("status is required"),
    });

    const [formButtonStatus, setFormButtonStatus] = React.useState({
        label: "Submit",
        loading: false,
        disabled: false,
    });

    const { register, handleSubmit, watch, formState: { errors }, control, Controller, setValue, clearErrors, getValues, reset } = useForm({
        resolver: yupResolver(scheme), 
        defaultValues: {
            email: '',
            phone: '',
            alt_phone_number: '',
            preferred_project_id: null,
            source_url: '',
            city: '',
            remarks: '',
            created_at: '',
            follow_up_date: null,
            referred_by: '',
            assign_to: null,
            profession: '',
            crm_status: null,
            age: null,
            country: null,
            ip_address: '',
        },
        mode: 'all'
    });

    // Fetch notes data
    const fetchNotes = React.useCallback(() => {
        if (id) {
            setNotesLoading(true);
            Notes.getNotes({ id: id })
                .then((response) => {
                    const notesData = response.data.data || [];
                    setNotesList(notesData);
                })
                .catch((err) => {
                    console.log("Error fetching notes:", err);
                    setNotesList([]);
                })
                .finally(() => {
                    setNotesLoading(false);
                });
        }
    }, [id]);

    // Fetch notes when Notes tab becomes active
    React.useEffect(() => {
        if (notesTabActive) {
            fetchNotes();
        }
    }, [notesTabActive, fetchNotes]);

    // Fetch notes when refreshNotes changes (when notes are added/updated)
    React.useEffect(() => {
        if (refreshNotes > 0) {
            fetchNotes();
        }
    }, [refreshNotes, fetchNotes]);

    // Fetch data only when ID changes, not when modal opens/closes
    React.useEffect(() => {
        if (id) {
            setLeadId(id);
            // Check if data is already cached
            if (fetchedDataCache.current[id]) {
                populateForm(fetchedDataCache.current[id]);
            } else {
                fetchQuickEditDetails();
            }
            // Fetch notes when modal opens initially
            if (open) {
                fetchNotes();
            }
        }
    }, [id, open]); // Depend on both id and open

    const populateForm = (data) => {
        const { lead, statusList } = data;
        const crmStatus = statusList?.crm_status || [];
        
        const matchedStatus = crmStatus.find(status => status.id === Number(lead.crm_status));
        
        setValue('email', lead?.email || '');
        setValue('phone', lead?.phone_number || '');
        setValue('alt_phone_number', lead?.alt_phone_number || '');
        setValue('preferred_project_id', lead?.preferred_project || null);
        setValue('source_url', lead?.source_url || '');
        setValue('city', lead?.city || '');
        setValue('remarks', lead?.remarks || '');
        setValue('created_at', lead?.created_at || '');
        setValue('follow_up_date', lead?.follow_up_date || null);
        setValue('referred_by', lead?.referred_by || '');
        setValue('assign_to', lead?.assigned_to_details || null);
        setValue('profession', lead?.profession || '');
        setValue('crm_status', matchedStatus || null);
        
        if (lead?.age_range != null) {
            setValue('age', {
                id: 0,
                name: lead?.age_range
            });
        } else {
            setValue('age', null);
        }
        
        setValue('country', lead?.country || null);
        
        setLoading(false);
    };

    const fetchQuickEditDetails = async (retryCount = 2) => {
        // Only show loading indicator when first fetching
        if (!fetchedDataCache.current[id]) {
            setLoading(true);
        }

        const fetchData = async (retries) => {
            try {
                // Fetch both status list and lead details in parallel
                const [statusResponse, leadResponse] = await Promise.all([
                    DynamicdropdownsAPI.get(),
                    Leads.getQuickEditDetails({ id: id })
                ]);

                if (leadResponse.data.data) {
                    const lead = leadResponse.data.data;
                    
                    // Cache the fetched data
                    fetchedDataCache.current[id] = {
                        lead,
                        statusList: statusResponse.data
                    };
                    
                    // Only populate form if component is still mounted/active
                    if (open) {
                        populateForm({ lead, statusList: statusResponse.data });
                    }
                } else {
                    setLoading(false);
                }
            } catch (err) {
                console.log(err.message);

                if (retries > 0) {
                    setTimeout(() => {
                        fetchData(retries - 1);
                    }, 1000);
                } else {
                    setLoading(false);
                    console.log("Failed to fetch details");
                }
            }
        };

        fetchData(retryCount);
    };

    const onSubmit = (data) => {
        setFormButtonStatus({ ...formButtonStatus, loading: true });
        const followdate = data.follow_up_date != null ? moment(data.follow_up_date).format("YYYY-MM-DD") : '';

        if (data.status === "New Enquiry") {
            data.status = "Open";
        }

        const dataToSubmit = {
            id: id || leadId,
            alt_phone_number: data.alt_phone_number,
            city: data.city,
            preferred_project_id: data.preferred_project_id?.id,
            follow_up_date: followdate,
            crm_status: data.crm_status?.id,
            age_range: data.age?.name || null,
            country_id: data.country?.id,
        };

        let action;
        if ((id && id > 0) || (leadId && leadId > 0)) {
            action = Leads.postQuickEdit(dataToSubmit);
        } else {
            toast.error("Invalid lead ID", { autoClose: 1000 });
            setFormButtonStatus({ label: "Submit", loading: false, disabled: false });
            return;
        }

        if (action) {
            action.then(async (response) => {
                if (response.data.message === "Updated") {
                    toast.success("Lead Successfully Saved!", { autoClose: 1000 });
                } else {
                    toast.success("Lead Successfully Saved!", { autoClose: 1000 });
                }

                setFormButtonStatus({ label: "Submit", loading: false, disabled: false });
                // Refresh the leads table after successful submission
                if (refreshLeadsTable) {
                    refreshLeadsTable();
                }
                handleClose();
            }).catch(errors => {
                console.log("error", errors);
                toast.error("Internal Server Error, please try again later!", { autoClose: 1000 });
                setFormButtonStatus({ label: "Submit", loading: false, disabled: false });
            });
        } else {
            toast.error("Failed to submit lead data", { autoClose: 1000 });
            setFormButtonStatus({ label: "Submit", loading: false, disabled: false });
        }
    };

    // Check if status should be enabled based on notes list
    const isStatusEnabled = notesList && notesList.length > 0;

    // Only render content when open
    if (!open) {
        return null;
    }

    return (
        <div>
            {loading || notesLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '30vh',
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item sm={6}>
                                <TextInput 
                                    control={control}
                                    name="email"
                                    label="Email"
                                    disabled={true}
                                />
                            </Grid>

                            <Grid item sm={6}>
                                <TextInput 
                                    control={control}
                                    name="phone"
                                    label="Phone Number"
                                    disabled={true}
                                />
                            </Grid>
                        </Grid>
                        
                        <Grid container spacing={2} mt={0.3}>
                            <Grid item sm={6}>
                                <TextInput 
                                    control={control}
                                    name="alt_phone_number"
                                    label="Alternate Phone Number"
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <SelectX
                                    options={fetchProjects}
                                    label="Preferred Project"
                                    name={'preferred_project_id'}
                                    control={control}
                                />
                            </Grid>
                        </Grid>
                        
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} sm={6}>
                                <SelectX
                                    options={fetchCountries}
                                    label="Country"
                                    name={'country'}
                                    control={control}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextInput 
                                    control={control} 
                                    name="city"
                                    label="City"
                                />
                            </Grid>
                        </Grid>
                        
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} sm={6}>
                                <SelectX
                                    options={fetchStatus}
                                    label="Status"
                                    name={'crm_status'}
                                    control={control}
                                    type={isStatusEnabled ? '' : 'read-only'} // Enable/disable based on notes
                                    error2={errors?.crm_status?.message}
                                    // Add visual indication when disabled
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            backgroundColor: isStatusEnabled ? 'white' : '#f5f5f5',
                                            cursor: isStatusEnabled ? 'default' : 'not-allowed'
                                        })
                                    }}
                                />
                                {!isStatusEnabled && (
                                    <Typography variant="caption" color="textSecondary">
                                        Status can be changed only after adding notes
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SelectX
                                    options={fetchProfessionals}
                                    label="Profession"
                                    name={'profession'}
                                    control={control}
                                />
                            </Grid>
                        </Grid>
                        
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} sm={6}>
                                <SelectX
                                    options={fetchAgeList}
                                    label="Age"
                                    name={'age'}
                                    control={control}
                                    error2={errors?.crm_lead_type_id?.message}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <DateIn
                                    control={control}
                                    name="follow_up_date"
                                    label="Next Follow Up Date"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    
                    <DialogActions>
                        <Button onClick={handleClose} color="error" variant='outlined'>
                            Close
                        </Button>
                        <LoadingButton 
                            loading={formButtonStatus.loading} 
                            disabled={formButtonStatus.disabled}
                            variant="outlined" 
                            type="submit"
                        >
                            {formButtonStatus.label}
                        </LoadingButton>
                    </DialogActions>
                </form>
            )}
        </div>
    );
}